import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import {
  FiClock,
  FiTrendingUp,
  FiActivity,
  FiPlay,
  FiStopCircle,
  FiCheckCircle,
  FiCoffee,
  FiDollarSign,
  FiZap,
} from "react-icons/fi";

const WorkHourCounter = () => {
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [dailyGoal] = useState(8); // hours

  const timerRef = useRef(null);

  const dailyStats = {
    hoursWorked: 7.8,
    productiveTime: 86,
    breaksTaken: 3,
    earnings: 156.5,
    streak: 14,
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startTimer = () => {
    if (!timerRef.current) {
      setIsTimerActive(true);
      timerRef.current = setInterval(() => {
        setTimerSeconds((prev) => prev + 1);
      }, 1000);
    }
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsTimerActive(false);
    setTimerSeconds(0);
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const calculateDailyHours = () => {
    return timerSeconds / 3600;
  };

  const progressPercentage = (calculateDailyHours() / dailyGoal) * 100;

  const weeklyHours = [7.5, 8.2, 6.8, 8.5, 7.2, 4.5, 2.0];

  return (
    <ShowcaseContainer>
      <ContentWrapper>
        <HeaderSection>
          <Badge>
            <FiClock />
            <span>WORK HOUR TRACKER</span>
          </Badge>
          <MainTitle>
            Track Your <GradientText>Productivity</GradientText>
          </MainTitle>
          <Subtitle>
            Smart time tracking with detailed analytics and earnings
            calculation. Focus on your work while we track your progress.
          </Subtitle>
        </HeaderSection>

        <DashboardGrid>
          {/* Timer Card */}
          <TimerCard>
            <TimerHeader>
              <TimerTitle>
                Work Timer
                <TimerStatus active={isTimerActive}>
                  {isTimerActive ? "Active" : "Stopped"}
                </TimerStatus>
              </TimerTitle>
            </TimerHeader>

            <TimerDisplay>
              <TimeValue>{formatTime(timerSeconds)}</TimeValue>
              <TimeLabel>Work Time</TimeLabel>
            </TimerDisplay>

            <TimerControls>
              {!isTimerActive ? (
                <StartButton onClick={startTimer}>
                  <FiPlay />
                  <span>Start</span>
                </StartButton>
              ) : (
                <StartButton onClick={stopTimer} disabled={timerSeconds === 0}>
                  <FiStopCircle />
                  <span>Stop</span>
                </StartButton>
              )}
            </TimerControls>

            <TimerStats>
              <Stat>
                <label>Today's Total</label>
                <span>{calculateDailyHours().toFixed(1)}h</span>
              </Stat>
              <Stat>
                <label>Daily Goal</label>
                <span>{dailyGoal}h</span>
              </Stat>
              <Stat>
                <label>Progress</label>
                <span>{progressPercentage.toFixed(0)}%</span>
              </Stat>
            </TimerStats>

            <GoalProgress>
              <ProgressBar>
                <ProgressFill width={Math.min(progressPercentage, 100)} />
              </ProgressBar>
              <ProgressLabel>
                {progressPercentage >= 100 ? (
                  <span className="achieved">
                    <FiCheckCircle /> Goal Achieved!
                  </span>
                ) : (
                  `${(dailyGoal - calculateDailyHours()).toFixed(1)}h to goal`
                )}
              </ProgressLabel>
            </GoalProgress>
          </TimerCard>

          {/* Today's Stats Card */}
          <StatsCard>
            <CardHeader>
              <FiActivity />
              <h3>Today's Stats</h3>
            </CardHeader>

            <StatsGrid>
              <StatCard highlight>
                <StatIcon>
                  <FiClock />
                </StatIcon>
                <StatContent>
                  <StatValue>{dailyStats.hoursWorked}h</StatValue>
                  <StatLabel>Hours Worked</StatLabel>
                </StatContent>
              </StatCard>

              <StatCard>
                <StatIcon>
                  <FiTrendingUp />
                </StatIcon>
                <StatContent>
                  <StatValue>{dailyStats.productiveTime}%</StatValue>
                  <StatLabel>Productivity</StatLabel>
                </StatContent>
              </StatCard>

              {/* <StatCard>
                <StatIcon>
                  <FiCoffee />
                </StatIcon>
                <StatContent>
                  <StatValue>{dailyStats.breaksTaken}</StatValue>
                  <StatLabel>Breaks Taken</StatLabel>
                </StatContent>
              </StatCard>

              <StatCard>
                <StatIcon>
                  <FiDollarSign />
                </StatIcon>
                <StatContent>
                  <StatValue>${dailyStats.earnings}</StatValue>
                  <StatLabel>Earnings</StatLabel>
                </StatContent>
              </StatCard> */}
            </StatsGrid>

            <StreakSection>
              <StreakBadge>
                <FiZap />
                <div>
                  <span>{dailyStats.streak} Day Streak - </span>
                  <small> Keep it going!</small>
                </div>
              </StreakBadge>

              <WeeklyProgress>
                <WeeklyLabel>This Week</WeeklyLabel>
                <WeekBars>
                  {weeklyHours.map((hours, index) => (
                    <WeekBar key={index}>
                      <Bar hours={hours} active={index === 3} />
                      <DayLabel>
                        {["M", "T", "W", "T", "F", "S", "S"][index]}
                      </DayLabel>
                    </WeekBar>
                  ))}
                </WeekBars>
              </WeeklyProgress>
            </StreakSection>
          </StatsCard>
        </DashboardGrid>
      </ContentWrapper>
    </ShowcaseContainer>
  );
};

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components (Light Mode)
const ShowcaseContainer = styled.section`
  padding: 4rem 5%;
  background: #f8f9ff;
  min-height: 100vh;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 3rem 4%;
  }

  @media (max-width: 480px) {
    padding: 2rem 3%;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 100px;
  color: #9281dd;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 1.5px;
  margin-bottom: 1.5rem;
  text-transform: uppercase;

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
    gap: 0.5rem;
  }
`;

const MainTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  line-height: 1.1;
  color: #1e293b;
  margin-bottom: 1rem;
  font-family: "Inter", sans-serif;

  @media (max-width: 768px) {
    font-size: 2.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.875rem;
  }
`;

const GradientText = styled.span`
  color: #9281dd;
  display: inline-block;
  margin: 0 8px;
`;

const Subtitle = styled.p`
  font-size: 1.125rem;
  line-height: 1.6;
  color: #64748b;
  max-width: 600px;
  margin: 0 auto;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9375rem;
  }
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 1024px) {
    gap: 1.5rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TimerCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;

  &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.08);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
  }
`;

const TimerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const TimerTitle = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TimerStatus = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 100px;
  background: ${({ active }) =>
    active ? "rgba(16, 185, 129, 0.1)" : "rgba(148, 163, 184, 0.1)"};
  color: ${({ active }) => (active ? "#059669" : "#64748b")};
  border: 1px solid
    ${({ active }) =>
      active ? "rgba(16, 185, 129, 0.2)" : "rgba(148, 163, 184, 0.2)"};
  width: fit-content;
`;

const TimerDisplay = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 1.5rem 0;
  background: #f8fafc;
  border-radius: 12px;
`;

const TimeValue = styled.div`
  font-size: 3.5rem;
  font-weight: 800;
  color: #1e293b;
  font-family: "Roboto Mono", monospace;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 2.75rem;
  }

  @media (max-width: 480px) {
    font-size: 2.25rem;
  }
`;

const TimeLabel = styled.div`
  font-size: 1rem;
  color: #64748b;
  margin-top: 0.5rem;
`;

const TimerControls = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const BaseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  border: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    padding: 0.875rem 1.25rem;
  }
`;

const StartButton = styled(BaseButton)`
  background: #9281dd;
  color: white;
  box-shadow: 0 4px 15px rgba(139, 92, 246, 0.25);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(139, 92, 246, 0.35);
  }
`;

const TimerStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;

  label {
    font-size: 0.75rem;
    color: #64748b;
    margin-bottom: 0.5rem;
  }

  span {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
  }
`;

const GoalProgress = styled.div`
  margin-top: 1.5rem;
`;

const ProgressBar = styled.div`
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ width }) => `${width}%`};
  background: #9281dd;
  border-radius: 4px;
  transition: width 0.5s ease;
`;

const ProgressLabel = styled.div`
  font-size: 0.875rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .achieved {
    color: #059669;
    font-weight: 600;
  }
`;

const StatsCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.8s ease-out 0.3s both;

  &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.08);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;

  svg {
    color: #9281dd;
    font-size: 1.5rem;
  }

  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  padding: 1.5rem;
  background: ${({ highlight }) =>
    highlight ? "rgba(139, 92, 246, 0.05)" : "#f8fafc"};
  border: 1px solid
    ${({ highlight }) => (highlight ? "rgba(139, 92, 246, 0.1)" : "#e2e8f0")};
  border-radius: 16px;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: ${({ highlight }) => (highlight ? "#8b5cf6" : "#cbd5e1")};
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
  }
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #9281dd;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 800;
  color: #1e293b;
  line-height: 1;

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #64748b;
  margin-top: 0.25rem;
`;

const StreakSection = styled.div`
  margin-top: 2rem;
`;

const StreakBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: linear-gradient(
    135deg,
    rgba(245, 158, 11, 0.1),
    rgba(217, 119, 6, 0.05)
  );
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 16px;
  margin-bottom: 1.5rem;

  svg {
    color: #f59e0b;
    font-size: 1.5rem;
  }

  span {
    font-weight: 600;
    color: #1e293b;
  }

  small {
    color: #d97706;
    font-size: 0.875rem;
  }
`;

const WeeklyProgress = styled.div`
  margin-top: 1.5rem;
`;

const WeeklyLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 0.75rem;
`;

const WeekBars = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  height: 100px;
  padding: 0 0.5rem;
`;

const WeekBar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
`;

const Bar = styled.div`
  width: 12px;
  height: ${({ hours }) => `${hours * 10}px`};
  background: ${({ active }) => (active ? "#8b5cf6" : "#c4b5fd")};
  border-radius: 6px 6px 0 0;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.9;
    transform: scale(1.05);
  }
`;

const DayLabel = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 600;
`;

export default WorkHourCounter;
